import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Checkbox,
  Collapse,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Pagination,
  Radio,
  Segmented,
  Select,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Typography,
} from 'antd'
import {
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  FileExcelOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  UpOutlined,
} from '@ant-design/icons'
import ErrorResult from '@/components/feedback/ErrorResult'
import {
  exportAfterSalesIssues,
  exportAfterSalesGroupV2,
  exportFilteredAfterSalesIssues,
} from '@/services/api/afterSales'
import {
  useArchiveAfterSalesGroupV2,
  useAfterSalesGroup,
  useAfterSalesGroups,
  useCreateAfterSalesIssueLegacy,
  useCreateAfterSalesIssueV2,
  useAfterSalesReviewDetail,
  useDecideAfterSalesReview,
  useStartAfterSalesGroupSyncV2,
  useUpdateAfterSalesIssue,
} from '@/modules/afterSales/queries'
import { useAuthStore } from '@/stores/authStore'
import type {
  AfterSaleIssue,
  AfterSaleIssueStatus,
  AfterSaleIssueUpdatePayload,
  AfterSaleProcessingStage,
  AfterSaleReviewCandidate,
} from '@/types/afterSales'
import './AfterSalesPage.css'

const { Text, Title, Paragraph } = Typography

const STATUS_META: Record<AfterSaleIssueStatus, { label: string; color: string }> = {
  PENDING: { label: '待处理', color: 'orange' },
  IN_PROGRESS: { label: '处理中', color: 'blue' },
  RESOLVED: { label: '已解决', color: 'green' },
  ARCHIVED: { label: '已归档', color: 'default' },
}

const STAGE_OPTIONS: Array<{ value: AfterSaleProcessingStage; label: string }> = [
  { value: 'PROCESSING', label: '处理中' },
  { value: 'BLOCKED', label: '受阻' },
  { value: 'VERIFYING', label: '验证中' },
  { value: 'REVIEW_PENDING', label: '待复核' },
]

type StatusFilter = 'ALL' | AfterSaleIssueStatus
type GroupSort = 'updated_desc' | 'issues_desc' | 'name_asc'
const DEFAULT_GROUP_PAGE_SIZE = 20

function formatDateTime(value?: string | null) {
  return value ? value.replace('T', ' ').slice(0, 19) : '-'
}

function isConflictError(error: unknown) {
  return typeof error === 'object'
    && error !== null
    && ('status' in error || 'code' in error)
    && ((error as { status?: number }).status === 409 || (error as { code?: string }).code === 'CONFLICT')
}

type ReviewTargetConflictItem = {
  itemKey?: string
  expectedItemVersion?: number
  currentItemVersion?: number
  currentStatus?: AfterSaleIssueStatus
  currentReasonCode?: string
}

function reviewTargetConflictMessage(error: unknown) {
  const errors = typeof error === 'object' && error !== null
    ? (error as { errors?: unknown }).errors
    : undefined
  const detail = Array.isArray(errors) && errors.length > 0 && typeof errors[0] === 'object'
    ? errors[0] as { items?: ReviewTargetConflictItem[] }
    : undefined
  const items = detail?.items || []
  if (!items.length) {
    return '选中候选的目标问题已发生变化，无法按原候选自动应用。请刷新后重新核对候选。'
  }
  const targets = items.slice(0, 3).map((item) => {
    const status = item.currentStatus ? STATUS_META[item.currentStatus]?.label || item.currentStatus : '状态未知'
    const version = item.currentItemVersion === undefined ? '' : `，当前版本 ${item.currentItemVersion}`
    return `${item.itemKey || '未知问题'}：${status}${version}`
  }).join('；')
  return `选中候选的目标问题已变化：${targets}。请取消选择冲突候选后重试其他候选，或按当前状态手工处理。`
}

function reviewRelatedIssues(
  review: AfterSaleReviewCandidate,
  issues: AfterSaleIssue[],
) {
  const keys = new Set(review.relatedItemKeys || review.itemKeys || [])
  return issues.filter((issue) => keys.has(issue.itemKey || issue.id))
}

const REVIEW_REASON_META: Record<string, string> = {
  AI_STATUS_EVIDENCE_STALE: 'AI 依据的消息早于该问题最近一次状态变更，提议基于过时证据',
  AI_DUPLICATE_MATCHED_ITEM: '多个 AI 候选指向同一问题',
  AI_RESOLUTION_NOT_EXPLICIT: 'AI 判定已解决但依据不明确',
  AI_RECURRENCE_NOT_EXPLICIT: 'AI 判定问题复发但依据不明确',
  AI_AMBIGUOUS_RESOLUTION_EVIDENCE: '解决证据存在歧义',
  AI_CANDIDATE_MATCH_AMBIGUOUS: 'AI 候选与问题的匹配不唯一',
  EVIDENCE_REVOKED_REVIEW_REQUIRED: 'AI 引用的消息已被撤回',
}

const REVIEW_REASON_PREFIXES: Array<[string, string]> = [
  ['AI_STATUS_EVIDENCE_REQUIRES_REVIEW', 'AI 状态变更证据需要人工复核'],
  ['AI_DUPLICATE_MATCHED_ITEM', '多个 AI 候选指向同一问题'],
]

function reviewReasonLabel(reasonCode?: string | null) {
  const code = (reasonCode || '').trim()
  if (!code) return 'AI 候选变更待人工确认'
  if (REVIEW_REASON_META[code]) return REVIEW_REASON_META[code]
  for (const [prefix, label] of REVIEW_REASON_PREFIXES) {
    if (code.startsWith(`${prefix}:`) || code === prefix) return label
  }
  return 'AI 候选变更待人工确认'
}

function isDegradedReview(review: AfterSaleReviewCandidate) {
  return !review.candidates?.length && !!review.candidateIds?.length
}

function defaultReviewCandidateIds(review: AfterSaleReviewCandidate) {
  return (review.candidates || [])
    .filter((candidate) => candidate.commandPatch != null)
    .map((candidate) => candidate.candidateId)
}

function formatReviewValue(value: unknown) {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function reviewFieldLabel(field: string) {
  return {
    title: '标题',
    description: '描述',
    status: '状态',
    nextAction: '下一步',
    solution: '解决方案',
    processingStage: '处理阶段',
    issueOwner: '负责人',
  }[field] || field
}

export default function AfterSalesPage() {
  const { message, modal } = AntdApp.useApp()
  const roles = useAuthStore((state) => state.roles)
  const [searchParams, setSearchParams] = useSearchParams()
  const [groupSearch, setGroupSearch] = useState('')
  const deferredGroupSearch = useDeferredValue(groupSearch.trim())
  const [groupSort, setGroupSort] = useState<GroupSort>('updated_desc')
  const [serviceOwner, setServiceOwner] = useState<string>()
  const [salesOwner, setSalesOwner] = useState<string>()
  const [groupPage, setGroupPage] = useState(1)
  const [groupPageSize, setGroupPageSize] = useState(DEFAULT_GROUP_PAGE_SIZE)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [issueSearch, setIssueSearch] = useState('')
  const [isExportingFilteredIssues, setIsExportingFilteredIssues] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const [editingIssue, setEditingIssue] = useState<AfterSaleIssue | null>(null)
  const [isCreatingIssue, setIsCreatingIssue] = useState(false)
  const [hasConflict, setHasConflict] = useState(false)
  const [expandedObservationIssues, setExpandedObservationIssues] = useState<Record<string, boolean>>({})
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null)
  const [selectedReviewCandidateIds, setSelectedReviewCandidateIds] = useState<string[]>([])
  const [reviewReason, setReviewReason] = useState('')
  const [form] = Form.useForm<AfterSaleIssueUpdatePayload>()
  const selectedRoomId = searchParams.get('room_id')

  const groupsQuery = useAfterSalesGroups({
    search: deferredGroupSearch,
    sort: groupSort,
    serviceOwner,
    salesOwner,
    page: groupPage,
    pageSize: groupPageSize,
  })
  const selectedGroup = useMemo(
    () => groupsQuery.data?.items.find((group) => group.roomId === selectedRoomId),
    [groupsQuery.data?.items, selectedRoomId],
  )
  const groupQuery = useAfterSalesGroup(selectedRoomId, selectedGroup, !groupsQuery.isLoading)
  const updateMutation = useUpdateAfterSalesIssue()
  const createMutation = useCreateAfterSalesIssueV2()
  const createLegacyMutation = useCreateAfterSalesIssueLegacy()
  const archiveMutation = useArchiveAfterSalesGroupV2()
  const syncMutation = useStartAfterSalesGroupSyncV2()
  const reviewDecisionMutation = useDecideAfterSalesReview()
  const reviewDetailQuery = useAfterSalesReviewDetail(selectedRoomId, selectedReviewId)
  const groupPaginationTotal = Math.max(groupsQuery.data?.total ?? groupPageSize, 1)
  const apiMode = groupQuery.data?.apiMode || 'legacy'
  const isV2Group = apiMode === 'v2'
  const canDecideReview = roles.some((role) => ['service_agent', 'manager', 'admin'].includes(role))
  const formStatus = Form.useWatch('status', form)

  useEffect(() => {
    if (!groupsQuery.data) return
    const groups = groupsQuery.data.items
    if (!groups.length) {
      if (selectedRoomId) {
        const next = new URLSearchParams(searchParams)
        next.delete('room_id')
        setSearchParams(next, { replace: true })
      }
      return
    }
    if (!selectedRoomId || !groups.some((group) => group.roomId === selectedRoomId)) {
      const next = new URLSearchParams(searchParams)
      next.set('room_id', groups[0].roomId)
      setSearchParams(next, { replace: true })
    }
  }, [groupsQuery.data, searchParams, selectedRoomId, setSearchParams])

  function changeGroupSearch(value: string) {
    setGroupSearch(value)
    setGroupPage(1)
  }

  function changeGroupSort(value: GroupSort) {
    setGroupSort(value)
    setGroupPage(1)
  }

  function changeServiceOwner(value?: string) {
    setServiceOwner(value)
    setGroupPage(1)
  }

  function changeSalesOwner(value?: string) {
    setSalesOwner(value)
    setGroupPage(1)
  }

  function changeGroupPage(page: number, pageSize: number) {
    setGroupPage(pageSize === groupPageSize ? page : 1)
    setGroupPageSize(pageSize)
  }

  function selectGroup(roomId: string) {
    const next = new URLSearchParams(searchParams)
    next.set('room_id', roomId)
    setSearchParams(next)
    setStatusFilter('ALL')
    setIssueSearch('')
    setEditingIssue(null)
    setIsCreatingIssue(false)
    setHasConflict(false)
    setSelectedReviewId(null)
    setReviewReason('')
  }

  function openReview(review: AfterSaleReviewCandidate) {
    setSelectedReviewId(review.reviewId)
    setSelectedReviewCandidateIds(defaultReviewCandidateIds(review))
    setReviewReason('')
  }

  function closeReview() {
    if (reviewDecisionMutation.isPending) return
    setSelectedReviewId(null)
    setSelectedReviewCandidateIds([])
    setReviewReason('')
  }

  function openEditor(issue: AfterSaleIssue) {
    setIsCreatingIssue(false)
    setHasConflict(false)
    setEditingIssue(issue)
    form.setFieldsValue({
      title: issue.title,
      description: issue.description || '',
      status: issue.status,
      sendEmail: issue.sendEmail,
      solution: issue.solution || '',
      expectedVersion: groupQuery.data?.version || 0,
      expectedItemVersion: issue.itemVersion,
      processingStage: issue.processingStage ?? undefined,
      nextAction: issue.nextAction || '',
      reason: '',
      issueOwner: issue.issueOwner || '',
    })
  }

  function openCreateEditor() {
    setEditingIssue(null)
    setIsCreatingIssue(true)
    setHasConflict(false)
    form.resetFields()
    form.setFieldsValue({
      title: '',
      description: '',
      status: 'IN_PROGRESS',
      sendEmail: true,
      solution: '',
      processingStage: 'PROCESSING',
      nextAction: '',
      reason: '',
      issueOwner: '',
      evidenceMsgId: '',
    })
  }

  function closeEditor() {
    setEditingIssue(null)
    setIsCreatingIssue(false)
    setHasConflict(false)
    form.resetFields()
  }

  const counts = useMemo(() => {
    const result = { ALL: 0, PENDING: 0, IN_PROGRESS: 0, RESOLVED: 0, ARCHIVED: 0 }
    for (const issue of groupQuery.data?.issues || []) {
      result.ALL += 1
      result[issue.status] += 1
    }
    return result
  }, [groupQuery.data])

  const pendingReviews = groupQuery.data?.reviews || []
  const selectedReview = pendingReviews.find((review) => review.reviewId === selectedReviewId)

  function submitReviewDecision(decision: 'ACCEPTED' | 'REJECTED') {
    if (!selectedRoomId || !selectedReview || !reviewDetailQuery.data || !canDecideReview) return
    if (reviewDetailQuery.data.status === 'EXPIRED') {
      message.warning('该复核已超过处理时限，自动失效，无法再操作')
      return
    }
    const unavailableCandidateIds = new Set(
      reviewDetailQuery.data.candidates
        .filter((candidate) => candidate.acceptable === false)
        .map((candidate) => candidate.candidateId),
    )
    const selectedUnavailable = selectedReviewCandidateIds.filter((id) => unavailableCandidateIds.has(id))
    if (decision === 'ACCEPTED' && selectedUnavailable.length) {
      setSelectedReviewCandidateIds((current) => current.filter((id) => !unavailableCandidateIds.has(id)))
      message.warning('已取消不可直接接受的展示型候选，请核对后重试')
      return
    }
    if (decision === 'ACCEPTED' && !selectedReviewCandidateIds.length) {
      message.warning('至少选择一个候选变更后才能接受')
      return
    }
    modal.confirm({
      title: decision === 'ACCEPTED' ? '接受选中的候选变更？' : '拒绝此复核？',
      content: decision === 'ACCEPTED'
        ? '选中的候选将由 wx_data 写入正式问题状态。'
        : '拒绝只会关闭复核，不会修改正式问题状态。',
      okText: decision === 'ACCEPTED' ? '确认接受' : '确认拒绝',
      cancelText: '取消',
      onOk: async () => {
        try {
          await reviewDecisionMutation.mutateAsync({
            roomId: selectedRoomId,
            reviewId: selectedReview.reviewId,
            payload: {
              decision,
              expectedReviewVersion: reviewDetailQuery.data.reviewVersion,
              candidateIds: decision === 'ACCEPTED' ? selectedReviewCandidateIds : [],
              reason: reviewReason.trim(),
            },
          })
          message.success(decision === 'ACCEPTED' ? '复核已接受' : '复核已拒绝')
          closeReview()
          await groupQuery.refetch()
        } catch (error) {
          if ((error as { code?: string })?.code === 'REVIEW_CANDIDATE_NOT_ACCEPTABLE') {
            message.warning('选中内容包含仅供参考、不能直接接受的候选，请取消该候选后重试')
            await reviewDetailQuery.refetch()
            return
          }
          if (isConflictError(error)) {
            const code = (error as { code?: string }).code
            if (code === 'REVIEW_TARGET_STATE_CONFLICT') {
              message.warning(reviewTargetConflictMessage(error))
              await Promise.all([groupQuery.refetch(), reviewDetailQuery.refetch()])
              return
            }
            if (code === 'STATE_HASH_CONFLICT') {
              message.warning('问题状态在该复核生成后已发生变化，无法自动应用候选。请参照候选内容手工更新问题，或直接拒绝该复核。')
              return
            }
            message.warning('复核版本已变化，请重新加载详情后再操作')
            await reviewDetailQuery.refetch()
            return
          }
          message.error(error instanceof Error ? error.message : '复核操作失败，请稍后重试')
          throw error
        }
      },
    })
  }

  function closeDegradedReview(review: AfterSaleReviewCandidate, expectedVersion?: number) {
    if (!selectedRoomId || !canDecideReview) return
    modal.confirm({
      title: '关闭此历史复核？',
      content: '该复核产生于系统升级前，候选内容已不完整。关闭后不会再出现在待处理列表，也不会影响正式问题状态。',
      okText: '确认关闭',
      cancelText: '取消',
      onOk: async () => {
        try {
          await reviewDecisionMutation.mutateAsync({
            roomId: selectedRoomId,
            reviewId: review.reviewId,
            payload: {
              decision: 'REJECTED',
              expectedReviewVersion: expectedVersion ?? review.reviewVersion ?? 0,
              candidateIds: [],
              reason: '降级复核清理：升级前数据，无候选内容',
            },
          })
          message.success('已关闭该历史复核')
          await groupQuery.refetch()
        } catch (error) {
          if (isConflictError(error)) {
            message.warning('复核版本已变化，请刷新后重试')
            await groupQuery.refetch()
            return
          }
          message.error(error instanceof Error ? error.message : '操作失败，请稍后重试')
          throw error
        }
      },
    })
  }

  const visibleIssues = useMemo(() => {
    const keyword = issueSearch.trim().toLowerCase()
    return (groupQuery.data?.issues || []).filter((issue) => {
      if (statusFilter !== 'ALL' && issue.status !== statusFilter) return false
      if (!keyword) return true
      return [
        issue.title,
        issue.description,
        issue.solution,
        issue.nextAction,
        issue.issueOwner,
        ...(issue.observations || []).map((observation) => observation.summary),
      ]
        .some((value) => String(value || '').toLowerCase().includes(keyword))
    })
  }, [groupQuery.data, issueSearch, statusFilter])

  const issueChanges = useMemo(
    () => (groupQuery.data?.changes || [])
      .filter((change) => (change.issueId || change.itemKey) === editingIssue?.id)
      .sort((a, b) => String(b.operatedAt || b.recordedAt || '').localeCompare(String(a.operatedAt || a.recordedAt || ''))),
    [editingIssue?.id, groupQuery.data],
  )

  async function saveIssue(values: AfterSaleIssueUpdatePayload) {
    if (!selectedRoomId || !groupQuery.data) return
    try {
      if (isCreatingIssue) {
        await (isV2Group ? createMutation : createLegacyMutation).mutateAsync({
          roomId: selectedRoomId,
          payload: {
            title: values.title,
            description: values.description,
            solution: values.solution,
            sendEmail: values.sendEmail,
            nextAction: values.nextAction || '',
            issueOwner: values.issueOwner || '',
            evidenceMsgId: values.evidenceMsgId || '',
            reason: values.reason?.trim() || '',
          },
        })
        message.success('售后问题已添加')
        closeEditor()
        await groupQuery.refetch()
        return
      }
      if (!editingIssue) return
      await updateMutation.mutateAsync({
        roomId: selectedRoomId,
        issueId: isV2Group ? (editingIssue.itemKey || editingIssue.id) : editingIssue.id,
        payload: {
          ...values,
          apiMode,
          originalIssue: editingIssue,
          expectedVersion: groupQuery.data.version,
          expectedItemVersion: editingIssue.itemVersion,
          processingStage: values.status === 'IN_PROGRESS' ? values.processingStage : null,
          nextAction: values.status === 'RESOLVED' || values.status === 'ARCHIVED'
            ? null
            : values.nextAction,
        },
      })
      message.success('售后问题已更新')
      closeEditor()
      await groupQuery.refetch()
    } catch (error) {
      if (isConflictError(error)) {
        setHasConflict(true)
        message.warning('服务器已有更新，当前表单已保留')
        await groupQuery.refetch()
        return
      }
      message.error(error instanceof Error ? error.message : '保存失败，请刷新后重试')
    }
  }

  async function loadLatestConflictVersion() {
    if (!editingIssue) return
    const result = await groupQuery.refetch()
    const latest = result.data?.issues.find((issue) => issue.id === editingIssue.id)
    if (latest) {
      openEditor(latest)
      message.success('已载入服务器最新版本')
    }
  }

  function archiveCurrentGroup() {
    if (!selectedRoomId) return
    modal.confirm({
      title: '归档当前群超期问题？',
      content: '系统将按 wx_data 既定规则归档符合条件的问题，归档记录仍可在“已归档”中查看。',
      okText: '确认归档',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await archiveMutation.mutateAsync(selectedRoomId)
          message.success(`已归档 ${result.archivedCount ?? 0} 个问题`)
          await groupQuery.refetch()
        } catch (error) {
          message.error(error instanceof Error ? error.message : '归档失败，请稍后重试')
          throw error
        }
      },
    })
  }

  async function exportCurrentGroupExcel() {
    if (!selectedRoomId || !groupQuery.data) return
    setIsExportingExcel(true)
    try {
      await exportAfterSalesGroupV2(selectedRoomId, groupQuery.data.groupName)
      message.success('当前群 Excel 已导出')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '当前群 Excel 导出失败')
    } finally {
      setIsExportingExcel(false)
    }
  }

  async function startSync() {
    if (!selectedRoomId) return
    try {
      const result = await syncMutation.mutateAsync(selectedRoomId)
      message.success(`同步任务已提交：${result.runId}`)
      groupQuery.refetch()
    } catch (error) {
      message.error(error instanceof Error ? error.message : '同步任务提交失败')
    }
  }

  function exportVisibleIssues() {
    if (!selectedGroup || !visibleIssues.length) return
    exportAfterSalesIssues(selectedGroup, visibleIssues)
    message.success(`已导出 ${visibleIssues.length} 个问题`)
  }

  async function exportFilteredIssues() {
    setIsExportingFilteredIssues(true)
    try {
      const exportedCount = await exportFilteredAfterSalesIssues({
        search: deferredGroupSearch,
        sort: groupSort,
        serviceOwner,
        salesOwner,
        status: statusFilter,
        issueSearch: issueSearch.trim(),
      })
      message.success(
        exportedCount
          ? `已导出筛选群中的 ${exportedCount} 个问题`
          : '筛选群问题已导出',
      )
    } catch (error) {
      message.error(error instanceof Error ? error.message : '筛选群问题导出失败')
    } finally {
      setIsExportingFilteredIssues(false)
    }
  }

  return (
    <div className="after-sales-page">
      <aside className="after-sales-groups">
        <div className="after-sales-groups-header">
          <div>
            <Title level={4}>售后问题管理</Title>
            <Text type="secondary">全部企业微信群问题</Text>
          </div>
          <Button
            type="text"
            icon={<ReloadOutlined />}
            aria-label="刷新群列表"
            onClick={() => groupsQuery.refetch()}
          />
        </div>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="搜索群名称或客户"
          value={groupSearch}
          onChange={(event) => changeGroupSearch(event.target.value)}
        />
        <div className="after-sales-owner-filters">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="按接待负责人筛选"
            value={serviceOwner}
            options={groupsQuery.data?.filterOptions?.serviceOwners || []}
            onChange={changeServiceOwner}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="按销售负责人筛选"
            value={salesOwner}
            options={groupsQuery.data?.filterOptions?.salesOwners || []}
            onChange={changeSalesOwner}
          />
        </div>
        <div className="after-sales-group-toolbar">
          <Text type="secondary">服务群 {groupsQuery.data?.total || 0}</Text>
          <Select<GroupSort>
            size="small"
            value={groupSort}
            onChange={changeGroupSort}
            options={[
              { value: 'updated_desc', label: '最近更新' },
              { value: 'issues_desc', label: '未解决优先' },
              { value: 'name_asc', label: '群名称' },
            ]}
          />
        </div>
        <Button
          block
          icon={<DownloadOutlined />}
          loading={isExportingFilteredIssues}
          disabled={!groupsQuery.data?.total}
          title="导出当前群搜索、负责人筛选以及问题状态和关键词共同命中的全部问题"
          onClick={exportFilteredIssues}
        >
          导出筛选群问题
        </Button>
        <div className="after-sales-group-list">
          {groupsQuery.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
          {groupsQuery.isError && (
            <ErrorResult error={groupsQuery.error} onRetry={() => groupsQuery.refetch()} />
          )}
          {!groupsQuery.isLoading && !groupsQuery.isError && !(groupsQuery.data?.items.length) && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无售后群数据" />
          )}
          {groupsQuery.data?.items.map((group) => (
            <button
              key={group.roomId}
              type="button"
              className={`after-sales-group-item${selectedRoomId === group.roomId ? ' active' : ''}`}
              onClick={() => selectGroup(group.roomId)}
            >
              <span className="after-sales-group-title">
                <strong>{group.groupName}</strong>
                <Badge count={group.openIssueCount} showZero color={group.openIssueCount ? '#1677ff' : '#bfbfbf'} />
              </span>
              <small>{group.customerName || group.roomId}</small>
              <small>
                接待：{group.serviceOwnerName || group.serviceOwner || '未配置'} · 销售：{group.salesOwner || '未配置'}
              </small>
              <small>
                共 {group.issueCount} 个问题 · {formatDateTime(group.updatedAt)}
                {group.authorityBackend === 'AFTER_SALE_V2' ? ' · V2' : ''}
              </small>
            </button>
          ))}
        </div>
        <Pagination
          className="after-sales-group-pagination"
          current={groupPage}
          disabled={groupsQuery.isFetching}
          pageSize={groupPageSize}
          total={groupPaginationTotal}
          pageSizeOptions={[10, 20, 50]}
          showSizeChanger
          simple
          size="small"
          onChange={changeGroupPage}
        />
      </aside>

      <main className="after-sales-workspace">
        {!selectedRoomId && <Empty description="请选择一个服务群" />}
        {selectedRoomId && groupQuery.isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
        {selectedRoomId && groupQuery.isError && (
          <ErrorResult error={groupQuery.error} onRetry={() => groupQuery.refetch()} />
        )}
        {groupQuery.data && (
          <>
            <header className="after-sales-workspace-header">
              <div>
                <Space>
                  <Text type="secondary">售后问题管理</Text>
                  {isV2Group && <Tag color="purple">V2 状态模型</Tag>}
                </Space>
                <Title level={3}>{groupQuery.data.groupName}</Title>
                <Space split={<span>·</span>} wrap>
                  <Text type="secondary">{groupQuery.data.customerName || '未填写客户'}</Text>
                  <Text type="secondary">群 ID：{groupQuery.data.roomId}</Text>
                  <Tag color={groupQuery.data.openIssueCount ? 'processing' : 'success'}>
                    {groupQuery.data.openIssueCount} 个待处理
                  </Tag>
                  {isV2Group && groupQuery.data.stateVersion !== undefined && (
                    <Tag>状态 v{groupQuery.data.stateVersion}</Tag>
                  )}
                </Space>
              </div>
              <Space wrap className="after-sales-header-actions">
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEditor}>
                  手工添加问题
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  disabled={!visibleIssues.length}
                  onClick={exportVisibleIssues}
                >
                  导出当前筛选 CSV ({visibleIssues.length})
                </Button>
                {isV2Group && (
                  <>
                    <Button
                      icon={<FileExcelOutlined />}
                      loading={isExportingExcel}
                      onClick={exportCurrentGroupExcel}
                    >
                      导出当前群 Excel
                    </Button>
                    <Button
                      icon={<InboxOutlined />}
                      loading={archiveMutation.isPending}
                      onClick={archiveCurrentGroup}
                    >
                      归档超期问题
                    </Button>
                    <Button icon={<SyncOutlined />} loading={syncMutation.isPending} onClick={startSync}>
                      手动同步
                    </Button>
                  </>
                )}
                <Button icon={<ReloadOutlined />} onClick={() => groupQuery.refetch()}>
                  刷新
                </Button>
              </Space>
            </header>

            <div className="after-sales-filters">
              <Segmented<StatusFilter>
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'ALL', label: `全部 ${counts.ALL}` },
                  { value: 'PENDING', label: `待处理 ${counts.PENDING + pendingReviews.length}` },
                  { value: 'IN_PROGRESS', label: `处理中 ${counts.IN_PROGRESS}` },
                  { value: 'RESOLVED', label: `已解决 ${counts.RESOLVED}` },
                  { value: 'ARCHIVED', label: `已归档 ${counts.ARCHIVED}` },
                ]}
              />
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="搜索问题内容"
                value={issueSearch}
                onChange={(event) => setIssueSearch(event.target.value)}
              />
            </div>

            <div className="after-sales-issue-list">
              {!visibleIssues.length && !(statusFilter === 'PENDING' && pendingReviews.length) && (
                <Empty description="当前筛选条件下没有问题" />
              )}
              {statusFilter === 'PENDING' && pendingReviews.map((review) => {
                const relatedIssues = reviewRelatedIssues(review, groupQuery.data?.issues || [])
                const degraded = isDegradedReview(review)
                return (
                  <article key={review.reviewId} className="after-sales-issue-card after-sales-review-card">
                    <div className="after-sales-issue-main">
                      <Space wrap>
                        <Tag color="gold">待复核</Tag>
                        <Text type="secondary">{formatDateTime(review.createdAt)}</Text>
                        {review.candidateIds?.length ? <Tag>候选 {review.candidateIds.length} 条</Tag> : null}
                      </Space>
                      <Title level={5}>{reviewReasonLabel(review.reasonCode)}</Title>
                      {review.reasonCode && <Text type="secondary" code>{review.reasonCode}</Text>}
                      <Paragraph type="secondary">
                        AI 提议的问题变更尚未写入正式状态，需要人工确认。接受后才会更新正式问题状态，拒绝不会影响现状。
                      </Paragraph>
                      {degraded ? (
                        <Alert
                          type="warning"
                          showIcon
                          message="该复核产生于系统升级前，候选内容已不完整"
                          description="无法恢复具体的变更内容，建议直接关闭。关闭后不会再出现在待处理列表，也不会影响正式问题状态。"
                          action={canDecideReview ? (
                            <Button
                              size="small"
                              danger
                              loading={reviewDecisionMutation.isPending}
                              onClick={() => closeDegradedReview(review)}
                            >
                              一键关闭
                            </Button>
                          ) : undefined}
                        />
                      ) : relatedIssues.length > 0 ? (
                        <div className="after-sales-review-related">
                          <Text strong>关联问题详情</Text>
                          {relatedIssues.map((issue) => (
                            <div key={issue.itemKey || issue.id} className="after-sales-review-related-item">
                              <Space wrap>
                                <Tag>{issue.itemKey || issue.id}</Tag>
                                <Tag color={STATUS_META[issue.status].color}>{STATUS_META[issue.status].label}</Tag>
                                <Text strong>{issue.title}</Text>
                              </Space>
                              {issue.nextAction && <Text type="secondary">下一步：{issue.nextAction}</Text>}
                              {(issue.observations || []).slice(-2).map((observation) => (
                                <Text key={observation.observationId || observation.summary} type="secondary">
                                  观察：{observation.summary}
                                </Text>
                              ))}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Alert
                          type="info"
                          showIcon
                          message="未能关联到具体问题条目"
                          description="可点击“查看复核详情”查看证据消息与候选变更内容。"
                        />
                      )}
                      {!degraded && (
                        <Collapse
                          ghost
                          size="small"
                          className="after-sales-review-tech"
                          items={[{
                            key: 'tech',
                            label: '技术信息',
                            children: (
                              <Space direction="vertical" size={2}>
                                {review.firstSeq !== undefined && review.lastSeq !== undefined && (
                                  <Text type="secondary">消息序号：{review.firstSeq} - {review.lastSeq}</Text>
                                )}
                                {review.inputHash && <Text type="secondary" code>输入快照：{review.inputHash}</Text>}
                                {review.baseStateHash && <Text type="secondary" code>基线快照：{review.baseStateHash}</Text>}
                              </Space>
                            ),
                          }]}
                        />
                      )}
                      <Space className="after-sales-review-actions" wrap>
                        {!degraded && (
                          <Button type="primary" onClick={() => openReview(review)}>
                            查看复核详情
                          </Button>
                        )}
                        {!canDecideReview && <Text type="secondary">当前账号没有人工决策权限</Text>}
                      </Space>
                    </div>
                  </article>
                )
              })}
              {visibleIssues.map((issue) => {
                const status = STATUS_META[issue.status]
                const observations = issue.observations || []
                const fallbackDescription = observations.at(-1)?.summary
                const observationsExpanded = Boolean(expandedObservationIssues[issue.id])
                return (
                  <article key={issue.id} className="after-sales-issue-card">
                    <div className="after-sales-issue-main">
                      <Space wrap>
                        <Tag color={status.color}>{status.label}</Tag>
                        {issue.sendEmail && <Tag>加入下次邮件</Tag>}
                        {issue.processingStage && <Tag>阶段：{issue.processingStage}</Tag>}
                        {issue.issueOwner && <Tag>负责人：{issue.issueOwner}</Tag>}
                        {issue.issueOrigin === 'MANUAL' && <Tag>人工加入</Tag>}
                        {issue.itemVersion !== undefined && <Tag>条目 v{issue.itemVersion}</Tag>}
                      </Space>
                      <Title level={5}>{issue.title}</Title>
                      <Paragraph ellipsis={{ rows: 2 }} type={issue.description ? undefined : 'secondary'}>
                        {issue.description || fallbackDescription || '暂无详细描述'}
                      </Paragraph>
                      {observations.length > 0 && (
                        <div className="after-sales-observations">
                          <div className="after-sales-observations-header">
                            <Text strong>观察摘要</Text>
                            <Space size={8}>
                              <Text type="secondary">{observations.length} 条</Text>
                              <Button
                                type="link"
                                size="small"
                                className="after-sales-observations-toggle"
                                icon={observationsExpanded ? <UpOutlined /> : <DownOutlined />}
                                aria-expanded={observationsExpanded}
                                aria-controls={`after-sales-observations-${issue.id}`}
                                onClick={() => setExpandedObservationIssues((current) => ({
                                  ...current,
                                  [issue.id]: !current[issue.id],
                                }))}
                              >
                                {observationsExpanded ? '收起' : '展开'}
                              </Button>
                            </Space>
                          </div>
                          {observationsExpanded && (
                            <div id={`after-sales-observations-${issue.id}`}>
                              {observations.map((observation, index) => (
                                <div
                                  key={observation.observationId || `${issue.id}-${index}`}
                                  className="after-sales-observation"
                                >
                                  <Text>{observation.summary}</Text>
                                  <Text type="secondary">{formatDateTime(observation.effectiveAt)}</Text>
                                  {(observation.evidenceMsgIds || []).map((msgId) => {
                                    const evidence = groupQuery.data.evidenceByMsgId?.[msgId]
                                    return (
                                      <div key={msgId} className="after-sales-evidence">
                                        <Text code>{msgId}</Text>
                                        <Text type="secondary">
                                          {evidence?.content || '原消息已清理或不可用'}
                                          {evidence?.sendTime ? ` · ${formatDateTime(evidence.sendTime)}` : ''}
                                        </Text>
                                      </div>
                                    )
                                  })}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {issue.solution && (
                        <div className="after-sales-solution">
                          <Text strong>解决方案：</Text>
                          <Text>{issue.solution}</Text>
                        </div>
                      )}
                      {issue.nextAction && (
                        <div className="after-sales-next-action">
                          <Text strong>下一步：</Text>
                          <Text>{issue.nextAction}</Text>
                        </div>
                      )}
                    </div>
                    <div className="after-sales-issue-side">
                      <Text type="secondary">更新于 {formatDateTime(issue.updatedAt)}</Text>
                      <Text type="secondary">
                        来源：{issue.issueOrigin || issue.updatedBy || '源数据同步'}
                      </Text>
                      <Button icon={<EditOutlined />} onClick={() => openEditor(issue)}>
                        查看与编辑
                      </Button>
                    </div>
                  </article>
                )
              })}
            </div>
          </>
        )}
      </main>

      <Drawer
        title="人工复核详情"
        width={720}
        open={Boolean(selectedReviewId)}
        destroyOnHidden
        onClose={closeReview}
        extra={selectedReview && <Tag color="gold">{reviewReasonLabel(selectedReview.reasonCode)}</Tag>}
      >
        {reviewDetailQuery.isLoading && <Skeleton active paragraph={{ rows: 8 }} />}
        {reviewDetailQuery.isError && (
          <ErrorResult error={reviewDetailQuery.error} onRetry={() => reviewDetailQuery.refetch()} />
        )}
        {reviewDetailQuery.data && (
          <div className="after-sales-review-detail">
            {reviewDetailQuery.data.status === 'EXPIRED' && (
              <Alert
                type="warning"
                showIcon
                message="该复核已超过处理时限，自动失效"
                description="候选变更基于的快照已过旧，不再允许接受。请以当前问题现状为准，后续同步如仍有疑问会生成新的复核。"
              />
            )}
            {reviewDetailQuery.data.degraded && reviewDetailQuery.data.status === 'PENDING' && (
              <Alert
                type="warning"
                showIcon
                message="该复核产生于系统升级前，候选内容已不完整"
                description="无法恢复具体的变更内容，建议直接关闭。关闭后不会再出现在待处理列表，也不会影响正式问题状态。"
                action={canDecideReview && selectedReview ? (
                  <Button
                    size="small"
                    danger
                    loading={reviewDecisionMutation.isPending}
                    onClick={() => closeDegradedReview(selectedReview, reviewDetailQuery.data?.reviewVersion)}
                  >
                    一键关闭
                  </Button>
                ) : undefined}
              />
            )}
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="复核版本">v{reviewDetailQuery.data.reviewVersion}</Descriptions.Item>
              <Descriptions.Item label="消息范围">
                {reviewDetailQuery.data.firstSeq ?? '-'} - {reviewDetailQuery.data.lastSeq ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="生成时间">{formatDateTime(reviewDetailQuery.data.createdAt)}</Descriptions.Item>
              {reviewDetailQuery.data.expireAt && (
                <Descriptions.Item label="处理时限">
                  {formatDateTime(reviewDetailQuery.data.expireAt)}
                  {reviewDetailQuery.data.status === 'PENDING' ? '（超时后自动失效）' : ''}
                </Descriptions.Item>
              )}
            </Descriptions>

            {!!reviewDetailQuery.data.currentItems?.length && (
              <>
                <Title level={5}>问题现状</Title>
                <Space direction="vertical" size={8} className="after-sales-review-current">
                  {reviewDetailQuery.data.currentItems.map((item) => {
                    const currentStatus = STATUS_META[item.status as AfterSaleIssueStatus]
                    return (
                      <div key={item.itemKey} className="after-sales-review-current-item">
                        <Space wrap>
                          <Tag>{item.itemKey}</Tag>
                          {item.status && (
                            <Tag color={currentStatus?.color}>{currentStatus?.label || item.status}</Tag>
                          )}
                          <Text strong>{item.title || '（无标题）'}</Text>
                        </Space>
                        {item.statusChangedAt && (
                          <Text type="secondary">
                            状态变更于 {formatDateTime(item.statusChangedAt)}
                            {item.statusChangedBy ? ` · 操作人：${item.statusChangedBy}` : ''}
                            {item.statusOrigin ? ` · 来源：${item.statusOrigin}` : ''}
                          </Text>
                        )}
                        {item.nextAction && <Text type="secondary">下一步：{item.nextAction}</Text>}
                      </div>
                    )
                  })}
                </Space>
              </>
            )}

            <Title level={5}>候选变更</Title>
            {!reviewDetailQuery.data.candidates.length && (
              <Text type="secondary">
                该复核未返回候选变更内容{reviewDetailQuery.data.degraded ? '（升级前数据，内容已不完整）' : ''}。
              </Text>
            )}
            <Space direction="vertical" size={12} className="after-sales-review-candidates">
              {reviewDetailQuery.data.candidates.map((candidate) => {
                const changedFields = candidate.changedFields || Array.from(new Set([
                  ...Object.keys(candidate.before || {}),
                  ...Object.keys(candidate.after || {}),
                ]))
                const before = candidate.before || {}
                const after = candidate.after || {}
                return (
                  <div key={candidate.candidateId} className="after-sales-review-candidate">
                    <Space wrap>
                      <Checkbox
                        disabled={!canDecideReview
                          || reviewDetailQuery.data.status !== 'PENDING'
                          || candidate.acceptable === false}
                        checked={selectedReviewCandidateIds.includes(candidate.candidateId)}
                        onChange={(event) => setSelectedReviewCandidateIds((current) => event.target.checked
                          ? [...current, candidate.candidateId]
                          : current.filter((id) => id !== candidate.candidateId))}
                      />
                      <Tag>{candidate.itemKey || candidate.candidateId}</Tag>
                      <Text strong>{candidate.candidateId}</Text>
                      {candidate.acceptable === false && <Tag color="orange">仅供参考，不能直接接受</Tag>}
                      {candidate.acceptable === false && candidate.reasonCode && (
                        <Text type="secondary" code>{candidate.reasonCode}</Text>
                      )}
                    </Space>
                    <Descriptions size="small" column={1} className="after-sales-review-change-table">
                      {changedFields.map((field) => (
                        <Descriptions.Item key={field} label={reviewFieldLabel(field)}>
                          <div className="after-sales-review-change-values">
                            <Text type="secondary">原值：{formatReviewValue(before[field])}</Text>
                            <Text>候选：{formatReviewValue(after[field])}</Text>
                          </div>
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                    {!!candidate.evidenceMsgIds?.length && (
                      <div className="after-sales-review-evidence-list">
                        <Text strong>证据消息</Text>
                        {candidate.evidenceMsgIds.map((msgId) => {
                          const evidence = reviewDetailQuery.data?.evidence.find((item) => item.msgId === msgId)
                          return (
                            <Text key={msgId} type="secondary">
                              {msgId}：{evidence?.content || '原消息已清理或不可用'}
                            </Text>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </Space>

            <Form layout="vertical" className="after-sales-review-decision-form">
              <Form.Item label="处理备注">
                <Input.TextArea
                  rows={3}
                  maxLength={1000}
                  showCount
                  value={reviewReason}
                  onChange={(event) => setReviewReason(event.target.value)}
                  placeholder="选填，记录人工核对依据"
                  disabled={!canDecideReview || reviewDetailQuery.data.status !== 'PENDING'}
                />
              </Form.Item>
              {reviewDetailQuery.data.status === 'PENDING' ? (
                <Space>
                  <Button
                    type="primary"
                    disabled={!canDecideReview || !selectedReviewCandidateIds.length}
                    loading={reviewDecisionMutation.isPending}
                    onClick={() => submitReviewDecision('ACCEPTED')}
                  >
                    接受选中候选
                  </Button>
                  <Button
                    danger
                    disabled={!canDecideReview}
                    loading={reviewDecisionMutation.isPending}
                    onClick={() => submitReviewDecision('REJECTED')}
                  >
                    拒绝复核
                  </Button>
                </Space>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message={
                    reviewDetailQuery.data.status === 'ACCEPTED'
                      ? '该复核已接受'
                      : reviewDetailQuery.data.status === 'EXPIRED'
                        ? '该复核已失效，无法再操作'
                        : '该复核已拒绝'
                  }
                />
              )}
            </Form>
          </div>
        )}
      </Drawer>

      <Drawer
        title={isCreatingIssue ? '手工添加售后问题' : '售后问题详情'}
        width={620}
        open={Boolean(editingIssue) || isCreatingIssue}
        destroyOnHidden
        onClose={closeEditor}
        extra={(
          <Text type="secondary">
            {isCreatingIssue ? '新问题将标记为人工加入' : isV2Group ? '写回 V2 状态模型' : '写回原 JSON 状态文件'}
          </Text>
        )}
      >
        {(editingIssue || isCreatingIssue) && (
          <Form form={form} layout="vertical" onFinish={saveIssue}>
            {isCreatingIssue && (
              <Alert
                className="after-sales-editor-alert"
                type="info"
                showIcon
                message="手工添加的问题将直接进入处理中，并标记为人工加入。"
              />
            )}
            {isV2Group && editingIssue && (
              <Alert
                className="after-sales-editor-alert"
                type="info"
                showIcon
                message={`当前条目版本：v${editingIssue.itemVersion ?? '-'}`}
                description="V2 保存会校验条目版本，发生冲突时需要刷新后重新确认。"
              />
            )}
            {hasConflict && (
              <Alert
                className="after-sales-editor-alert"
                type="warning"
                showIcon
                message="服务器已有更新"
                description="当前表单已保留，请载入最新版本后重新确认修改。"
                action={<Button size="small" onClick={loadLatestConflictVersion}>载入最新版本</Button>}
              />
            )}
            <Form.Item name="title" label="问题标题" rules={[{ required: true }, { max: 200 }]}>
              <Input />
            </Form.Item>
            <Form.Item name="description" label="详细描述" rules={[{ max: 5000 }]}>
              <Input.TextArea rows={5} />
            </Form.Item>
            {isV2Group && (
              <Form.Item name="issueOwner" label="问题解决负责人" rules={[{ max: 100 }]}>
                <Input placeholder="选填，填写问题解决负责人" />
              </Form.Item>
            )}
            <Form.Item name="status" label="处理状态" rules={[{ required: true }]}>
              <Radio.Group disabled={isCreatingIssue}>
                <Radio value="PENDING">待处理</Radio>
                <Radio value="IN_PROGRESS">处理中</Radio>
                <Radio value="RESOLVED">已解决</Radio>
                {isV2Group && <Radio value="ARCHIVED">已归档</Radio>}
              </Radio.Group>
            </Form.Item>
            {isV2Group && !isCreatingIssue && formStatus === 'IN_PROGRESS' && (
              <Form.Item name="processingStage" label="处理阶段">
                <Select
                  allowClear
                  placeholder="选择处理阶段"
                  options={STAGE_OPTIONS}
                />
              </Form.Item>
            )}
            <Form.Item name="solution" label="解决方案" rules={[{ max: 5000 }]}>
              <Input.TextArea rows={5} />
            </Form.Item>
            {isV2Group && (
              <Form.Item name="nextAction" label="下一步动作" rules={[{ max: 1000 }]}>
                <Input.TextArea
                  rows={3}
                  disabled={formStatus === 'RESOLVED' || formStatus === 'ARCHIVED'}
                />
              </Form.Item>
            )}
            {isCreatingIssue && isV2Group && (
              <Form.Item name="evidenceMsgId" label="证据消息 ID" rules={[{ max: 255 }]}>
                <Input placeholder="选填，关联一条原始群消息" />
              </Form.Item>
            )}
            <Form.Item name="sendEmail" valuePropName="checked">
              <Checkbox>将该问题加入下一次售后邮件</Checkbox>
            </Form.Item>
            {isV2Group && (
              <Form.Item name="reason" label="修改原因" rules={[{ max: 1000 }]}>
                <Input.TextArea rows={3} placeholder="选填；未填写时由服务端记录默认原因" />
              </Form.Item>
            )}
            <Form.Item name="expectedVersion" hidden><Input /></Form.Item>
            <Form.Item name="expectedItemVersion" hidden><Input /></Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={updateMutation.isPending || createMutation.isPending || createLegacyMutation.isPending}
            >
              {isCreatingIssue ? '添加问题' : '保存修改'}
            </Button>

            {editingIssue && (
              <>
                <Descriptions
                  className="after-sales-source-meta"
                  size="small"
                  column={1}
                  items={[
                    { key: 'first', label: '首次发现', children: formatDateTime(editingIssue.firstSeenAt) },
                    { key: 'last', label: '最近发现', children: formatDateTime(editingIssue.lastSeenAt) },
                    { key: 'updated', label: '最近修改', children: formatDateTime(editingIssue.updatedAt) },
                    ...(isV2Group ? [
                      { key: 'issue-origin', label: '问题来源', children: editingIssue.issueOrigin || '-' },
                      { key: 'origin', label: '状态来源', children: editingIssue.statusOrigin || '-' },
                      { key: 'reason', label: '状态原因', children: editingIssue.statusReason || editingIssue.statusReasonCode || '-' },
                    ] : []),
                  ]}
                />

                <Title level={5} className="after-sales-change-title">状态变更记录</Title>
                {issueChanges.length ? (
                  <Timeline
                    items={issueChanges.map((change) => ({
                      color: change.origin === 'MANUAL' ? 'blue' : 'gray',
                      children: (
                        <div>
                          <Text strong>{change.actor || change.origin || '系统'}</Text>
                          <div>
                            <Text type="secondary">
                              {formatDateTime(change.operatedAt || change.recordedAt)} · {change.origin || '-'}
                            </Text>
                          </div>
                          {change.after?.status && <Tag color={STATUS_META[change.after.status].color}>{STATUS_META[change.after.status].label}</Tag>}
                          {change.changedFields?.length ? <Text type="secondary">字段：{change.changedFields.join('、')}</Text> : null}
                          {change.outcome && <Text type="secondary"> {change.outcome}</Text>}
                          {(change.reason || change.reasonCode) && (
                            <div><Text type="secondary">原因：{change.reason || change.reasonCode}</Text></div>
                          )}
                        </div>
                      ),
                    }))}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无变更记录" />
                )}
              </>
            )}
          </Form>
        )}
      </Drawer>
    </div>
  )
}
